import { useCallback, useEffect, useMemo, useState } from 'react';
import { INFT } from '../../types/nft';
import { useWeb3React } from '@web3-react/core';
import { useDispatch } from 'react-redux';
import { storeNumTokens } from '../../ducks/nft/actions';
import { getNFTsByWallet } from '../../services/OpenSea/getNFTsByWallet';
import { getNFTsByWallet as getReservoirData } from '../../services/Reservoir/getNftsByWallet';
import { getNFTsByWallet as getAlchemyData } from '../../services/Alchemy/getNftsByWallet';

interface UserNFTData {
  loading: boolean;
  error?: string;
  data?: INFT[];
  customAPIloading: boolean;
}

export const useUserNFTs = (): UserNFTData => {
  const dispatch = useDispatch();
  const { account, chainId } = useWeb3React();
  const [userNFTData, setUserNFTData] = useState<UserNFTData>({ loading: true, customAPIloading: true });
  const [reservoirData, setReservoirData] = useState<UserNFTData>({ loading: true, customAPIloading: true });
  const [alchemyData, setAlchemyData] = useState<UserNFTData>({ loading: true, customAPIloading: true });
  const [hasOSApiFailed, setHasOSApiFailed] = useState<boolean>(false);
  const [hasReservoirApiFailed, setHasReservoirApiFailed] = useState<boolean>(false);

  const assetCallback = useCallback((assets: INFT[], customAPIloading: boolean): void => {
    setUserNFTData({
      loading: false,
      data: assets,
      customAPIloading: customAPIloading,
    });
    dispatch(storeNumTokens(assets.length));
  }, []);

  const assetCallbackReservoir = useCallback((assets: INFT[], customAPIloading: boolean): void => {
    setReservoirData({
      loading: false,
      data: assets,
      customAPIloading: customAPIloading,
    });
    dispatch(storeNumTokens(assets.length));
  }, []);

  const assetCallbackAlchemy = useCallback((assets: INFT[], customAPIloading: boolean): void => {
    setAlchemyData({
      loading: false,
      data: assets,
      customAPIloading: customAPIloading,
    });
    dispatch(storeNumTokens(assets.length));
  }, []);

  useEffect(() => {
    // Opensea API call
    (async (): Promise<void> => {
      if (!account) return;
      try {
        let allAssets: INFT[] = [];
        await getNFTsByWallet(
          account,
          (assets) => {
            allAssets = [...allAssets, ...assets];
            assetCallback(allAssets, !!assets.length);
          },
          chainId,
        );
      } catch (e) {
        console.log('Opensea API fetch failed ', e);
        setHasOSApiFailed(true);
      }
    })();

    // Reservoir API call
    (async (): Promise<void> => {
      if (!account) return;
      try {
        let allReservoirAssets: INFT[] = [];
        await getReservoirData(
          account,
          (assets) => {
            allReservoirAssets = [...allReservoirAssets, ...assets];
            assetCallbackReservoir(allReservoirAssets, !!assets.length);
          },
          chainId,
        );
      } catch (e) {
        console.log('Reservoir API fetch failed ', e);
        setHasReservoirApiFailed(true);
      }
    })();

    // Alchemy API call - only called if OS or Reservoir api calls fail
    (async (): Promise<void> => {
      if (!account) return;
      if (hasOSApiFailed || hasReservoirApiFailed) {
        try {
          let allAlchemyAssets: INFT[] = [];
          await getAlchemyData(
            account,
            (assets) => {
              allAlchemyAssets = [...allAlchemyAssets, ...assets];
              assetCallbackAlchemy(allAlchemyAssets, !!assets.length);
            },
            chainId,
          );
        } catch (e) {
          console.log('Alchemy API fetch failed ', e);
        }
      }
    })();
  }, [hasOSApiFailed, hasReservoirApiFailed]);

  const fullData = useMemo(() => addNFTAmount(hasOSApiFailed, userNFTData, reservoirData, alchemyData), [
    userNFTData,
    reservoirData,
    alchemyData,
    hasOSApiFailed,
    hasReservoirApiFailed,
  ]);

  return fullData;
};

const addNFTAmount = (
  hasOSApiFailed: boolean,
  nftData: UserNFTData,
  reservoirData: UserNFTData,
  alchemyData?: UserNFTData,
): UserNFTData => {
  // if OS is down, get Alchemy data
  if (hasOSApiFailed && alchemyData?.data) return alchemyData;

  // get the amount for ERC1155 tokens from Reservoir or Alchemy
  const fallbackProvider = reservoirData.data && reservoirData?.data.length > 0 ? reservoirData : alchemyData;
  if (fallbackProvider?.data && nftData?.data) {
    return {
      ...nftData,
      data: nftData.data.map((apiNFT) => {
        const nftMatch =
          fallbackProvider?.data &&
          fallbackProvider?.data.find(
            (resNFT) => resNFT.contractAddress === apiNFT.contractAddress && resNFT.tokenId === apiNFT.tokenId,
          );
        if (nftMatch) {
          return { ...apiNFT, amount: nftMatch.amount ?? 0 };
        }
        return apiNFT;
      }),
    };
  }

  // in case everything fails
  return {
    loading: false,
    error: 'Providers not returning data.',
    data: [],
    customAPIloading: false,
  };
};
